import React, { useMemo, useRef, useState, useEffect } from 'react';
import { dailyRosterTimeSlot } from '@/constants';
import RowContent from '@/components/DailyRoster/RowContent';
import useInstructorRosterStore from '@/stores/instructorRosterStore';
import {
  Lesson,
  InstructorRosterInstructor
} from '@/constants/Interfaces/CommonInterfaces';
import InstructorCalendarSkeleton from '@/components/Skeletons/InstructorCalendarSkeleton';
import {
  getAllDatesInMonth,
  getDayNumber,
  groupBookingsByDate
} from '@/utils/common';
import { useParams } from 'react-router-dom';

interface InstructorRosterTableProps {
  instructorDetails: any;
}

const InstructorRosterTable: React.FC<InstructorRosterTableProps> = ({
  instructorDetails
}) => {
  const { id } = useParams();
  const { lessonSchedule, selectedDate, isLoading } =
    useInstructorRosterStore();
  const tableRef = useRef<HTMLTableElement>(null);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  // Build list of dates (YYYY-MM-DD) for the selected month
  const monthDates = useMemo(() => {
    if (!selectedDate) return [] as string[];
    return getAllDatesInMonth(selectedDate);
  }, [selectedDate]);

  // Build a flat list of lessons and group once per render for efficient lookup
  const flatLessons = useMemo(() => {
    if (!lessonSchedule || lessonSchedule.length === 0) return [];

    // Check if lessons already have instructor info
    const firstItem = lessonSchedule[0] as any;
    if (firstItem?.instructor) {
      return lessonSchedule as unknown as Lesson[];
    }

    // If grouped by instructor
    if (firstItem?.lessons && Array.isArray(firstItem.lessons)) {
      return (
        lessonSchedule as Array<{
          instructor?: InstructorRosterInstructor;
          lessons: Lesson[];
        }>
      ).flatMap(inst =>
        inst.lessons.map(lesson => ({
          ...lesson,
          instructor: inst.instructor
        }))
      );
    }
    // If the array already looks like a flat list of lessons
    const isFlat = (lessonSchedule as Array<unknown>).every(item => {
      const obj = item as { start?: string; string?: string };
      return typeof obj?.start === 'string' || typeof obj?.string === 'string';
    });
    if (isFlat) return lessonSchedule as unknown as Lesson[];

    // If items are grouped under { date, lessons }
    const hasDateLessons = (lessonSchedule as Array<unknown>).every(item => {
      const obj = item as { date?: string; lessons?: Lesson[] };
      return 'lessons' in (obj || {});
    });
    if (hasDateLessons) {
      return (lessonSchedule as Array<{ lessons?: Lesson[] }>)
        .flatMap(x => (Array.isArray(x.lessons) ? x.lessons : []))
        .filter(Boolean) as Lesson[];
    }

    // Else, assume an array of instructors, each with lessons
    return (lessonSchedule as Array<{ lessons?: Lesson[] }>)
      .flatMap(x => (Array.isArray(x.lessons) ? x.lessons : []))
      .filter(Boolean) as Lesson[];
  }, [lessonSchedule]);

  const lessonsByDate = useMemo(
    () => groupBookingsByDate(flatLessons),
    [flatLessons]
  );

  const getLessonsForDate = (date: string): Lesson[] => {
    return (lessonsByDate as Record<string, Lesson[]>)[date] || [];
  };

  // Calculate dynamic column widths based on content
  useEffect(() => {
    if (!tableRef.current || isLoading || !monthDates.length) return;

    const timeoutId = setTimeout(() => {
      const table = tableRef.current;
      if (!table) return;

      const tbody = table.querySelector('tbody');
      if (!tbody) return;

      const rows = tbody.querySelectorAll('tr');
      const timeSlotCount = dailyRosterTimeSlot.filter(t => t.value).length;

      // Initialize with context-aware defaults
      const maxWidths: number[] = new Array(timeSlotCount)
        .fill(0)
        .map((_, index) => {
          if (index === 0) return 120; // Name column needs more space
          if (index >= 1 && index <= 4) return 35; // Attribute columns can be compact
          return 45; // Standard time slots
        });

      // Iterate through each row to find max content width per column
      rows.forEach(row => {
        const cells = row.querySelectorAll('td:not(:first-child)');
        cells.forEach((cell, colIndex) => {
          if (colIndex < timeSlotCount) {
            // Get the actual content width by measuring text content in all relevant elements
            const contentElements = cell.querySelectorAll('div, button, span');
            contentElements.forEach(element => {
              const textContent = element.textContent?.trim() || '';
              if (textContent) {
                // Create temporary element to measure text width
                const span = document.createElement('span');
                span.style.visibility = 'hidden';
                span.style.position = 'absolute';
                span.style.whiteSpace = 'nowrap';
                span.style.fontSize = '12px'; // text-xs is 12px
                span.style.fontWeight = '600'; // Match font-semibold
                span.style.fontFamily = 'inherit'; // Inherit font family
                span.style.padding = '0 8px'; // account for cell padding
                span.textContent = textContent;
                document.body.appendChild(span);
                const width = span.offsetWidth;
                document.body.removeChild(span);

                // Update max width for this column (capped at 400px)
                maxWidths[colIndex] = Math.min(
                  400,
                  Math.max(maxWidths[colIndex], width)
                );
              }
            });
          }
        });
      });

      setColumnWidths(maxWidths);
    }, 0); // Small delay to ensure DOM is fully rendered

    return () => clearTimeout(timeoutId);
  }, [monthDates, lessonsByDate, isLoading]);

  return (
    <table ref={tableRef} className='w-full border-collapse min-w-max'>
      <thead className='sticky top-0 bg-gray-100 z-10'>
        <tr>
          <th className='border-b border-r p-2 font-semibold whitespace-nowrap bg-gray-100 sticky left-0 z-20 max-w-[210px] min-w-[150px]'>
            Date
          </th>
          {dailyRosterTimeSlot
            ?.filter(t => t.value)
            .map((time, index) => (
              <th
                key={time?.value}
                className='border-b border-r text-center font-semibold text-xs text-slate-600 bg-gray-100 sticky top-0 z-20'
                style={{
                  minWidth: `${columnWidths[index] || 45}px`,
                  maxWidth: '400px',
                  width: `${columnWidths[index] || 45}px`
                }}
              >
                {time?.value}
              </th>
            ))}
        </tr>
      </thead>
      {isLoading ? (
        <InstructorCalendarSkeleton />
      ) : (
        <tbody>
          {monthDates.map(date => {
            // Construct a faux instructor for RowContent with lessons for the given date
            const isDayOffArr =
              instructorDetails?.userDetail?.days_off
                ?.split(',')
                ?.map((d: string) => Number(d.trim())) || [];

            const isDayOff = isDayOffArr?.includes(getDayNumber(date)) || false;
            const fauxInstructor: InstructorRosterInstructor = {
              slug:
                (lessonSchedule?.[0] as any)?.instructor?.slug ||
                (lessonSchedule?.[0] as any)?.slug ||
                '',
              name:
                (lessonSchedule?.[0] as any)?.instructor?.name ||
                (lessonSchedule?.[0] as any)?.name ||
                'Unknown',
              day_off: isDayOff,
              id: Number(id),
              lessons: getLessonsForDate(date)
            };
            return (
              <tr key={date} className='border-t hover:bg-gray-50 m-0 p-0'>
                <td className='border p-1 max-w-[206] min-w-[150px] text-xs bg-white sticky left-0 z-10 font-medium pl-3'>
                  {date}
                </td>
                <RowContent
                  instructor={
                    fauxInstructor as unknown as InstructorRosterInstructor
                  }
                  rowDate={date}
                  columnWidths={columnWidths}
                />
              </tr>
            );
          })}
        </tbody>
      )}
    </table>
  );
};

export default InstructorRosterTable;
